// =================================================================================================
import { ReactNode, Component, PureComponent, ReactElement } from 'react';
import _ from 'lodash';
import { IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDescription, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { colorInterpolate, decomposeColor, opacity } from "@Utils/Colors";
import { isFunction, isDefined, isStringEmpty, IsNullOrEmpty } from '@Utils/Micro';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { WebThingPort, WebThing } from '@API/WebThing';
import { WebNode } from '@API/WebNode';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { Plegma } from '@API/Api';
import { Heatmap as PrimitiveHeatmap } from '@Primitive/Heatmap';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import * as WidgetsThingsStructs from '../WidgetsThingsStructs';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
// =================================================================================================
export interface HeatmapData {
    /**
     * Heatmap's main color
     */
    BgColor: string;

    /**
     * Color of axis labels
     */
    AxisFontColor: string;

    Cell: {
        W: number,
        H: number,
        Square: boolean;
        Unit: string;
    };

    XLabelsLocation: 'top' | 'bottom';

    YLabels: {
        Visible: boolean;
        Width: number;
        Align: 'right' | 'left' | 'center'
    }

    Legend: {
        Visible: boolean;
        Width: number;
        Items: LegendItem[];
        UseMainColor: boolean;
    }
}

interface LegendItem {
    label: string;
    color: string;
    invisible: boolean;
    value: number;
}
// -------------------------------------------------------------------------------------------------

export interface HeatmapDataJson extends LegendInfo {
    /**
     * Labels along X-axis.
     * E.g.: ['1am', '2am', '3am']
     */
    xLabels: (number | string)[];

    /**
     * Labels along Y-axis.
     * E.g.: ['Sun', 'Mon']
     */
    yLabels: (number | string)[];

    /**
     * 2D Array of numbers having yLabels.length rows and xLabels.length rows	
     * E.g.: [[2,3,5][5,6,9]]
     */
    data: number[][];

}

interface LegendInfo {
    /**
     * Legend items
     */
    legend: LegendItem[];

    /**
     * Runtime palette
     */
    palette: (number) => string;

    /**
     * Runtime palette's minimum value
     */
    paletteMin: number;

    /**
     * Runtime palette's maximum value
     */
    paletteMax: number;
}

interface SelectedCellJson {
    data: number
    xLabel: string;
    yLabel: string;
    xi: number;
    yi: number
}

// -------------------------------------------------------------------------------------------------
interface HeatmapState {
    heatmapData: HeatmapDataJson;
    selectedCell: [number, number];
}
// -------------------------------------------------------------------------------------------------
class Heatmap extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<HeatmapData>, HeatmapState> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<HeatmapData>) {
        super(props);
        this.state = {
            heatmapData: {
                xLabels: [],
                yLabels: [],
                data: [],
                ...this._getLegendInfo(this.props.WidgetData)
            },
            selectedCell: null
        }
    }

    // ---------------------------------------------------------------------------------------------
    componentDidMount() {
        this._UpdateData(this.props);
    }
    // ---------------------------------------------------------------------------------------------
    componentWillReceiveProps(nextProps: WidgetsThingsStructs.PartialIWidgetThingsProps<HeatmapData>) {
        if (!_.isEqual(this.props.WidgetData.Legend, nextProps.WidgetData.Legend) ||
            !_.isEqual(this.props.WidgetData.BgColor, nextProps.WidgetData.BgColor))
            this.setState((s: HeatmapState) => {
                const legendInfo = this._getLegendInfo(nextProps.WidgetData);
                s.heatmapData.legend = legendInfo.legend;
                s.heatmapData.palette = legendInfo.palette;
                s.heatmapData.paletteMax = legendInfo.paletteMax;
                s.heatmapData.paletteMin = legendInfo.paletteMin;
            });
        this._UpdateData(nextProps);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        // check if any data to draw
        const { width, height } = this.props;
        const { data, xLabels, yLabels, legend } = this.state.heatmapData;

        if (!isDefined(data) || data.length == 0)
            return WidgetsThingsStructs.DefaultMessageOnNoData(width, height);

        const { WidgetData } = this.props;
        const { Cell, BgColor, YLabels, Legend } = WidgetData;

        return <div style={{
            color: WidgetData.AxisFontColor,
            width: width,
            height: height
        }}>
            <PrimitiveHeatmap
                width={width}
                height={height}

                data={data}
                xLabels={xLabels}
                yLabels={yLabels}
                selectedCell={this.state.selectedCell}

                xLabelsLocation={WidgetData.XLabelsLocation}

                displayYLabels={YLabels.Visible}
                yLabelWidth={YLabels.Width}
                yLabelTextAlign={YLabels.Align}

                squares={Cell.Square}
                minCellWidth={Cell.W}
                minCellHeight={Cell.Square ? Cell.W : Cell.H}

                background={BgColor}
                unit={Cell.Unit}
                onClick={this._onClick}
                cellRender={this._cellRender}
                cellStyle={this._cellStyle}

                // legend
                legendPairs={Legend.Visible ? legend : null}
                legendLabelWidth={Legend.Width}
            />
        </div>;
    }
    // ---------------------------------------------------------------------------------------------
    private _UpdateData(props: WidgetsThingsStructs.PartialIWidgetThingsProps<HeatmapData>) {
        //try get `PortsStates`
        if (!isDefined(props.PortsStates))
            return;

        // foreach port
        _.forEach(props.PortsStates, (port, portKey) => {

            // check port key
            if (portKey == PortKeys.data) {

                // try get port state
                let heatmapData: HeatmapDataJson;
                if (port.LastStateStatus == StateStatus.NoData || isStringEmpty(port.State)) {
                    // fallback to default data
                    heatmapData = _.cloneDeep(props.WidgetDefaultValues[PortKeys.data]);
                    if (!isDefined(heatmapData))
                        return;
                }

                else if (port.LastStateStatus == StateStatus.Valid) {
                    // get data from port state
                    heatmapData = _.cloneDeep(port.State);
                }
                else
                    return;

                // ensure yLabels array is defined
                if (!isDefined(heatmapData.yLabels)) {
                    const yLength = isDefined(heatmapData.data) ? heatmapData.data.length : 0;
                    heatmapData.yLabels = _.map(_.range(yLength), () => '');
                }

                // ensure xLabels is defined
                if (!isDefined(heatmapData.xLabels)) {
                    const xLength = isDefined(heatmapData.data) && isDefined(heatmapData.data[0]) ? heatmapData.data[0].length : 0;
                    heatmapData.xLabels = _.map(_.range(xLength), () => '');
                }

                // handle legend (if needed)
                const { WidgetData } = this.props;
                // get legend info
                const legendInfo = this._getLegendInfo(WidgetData, heatmapData.legend);

                // update heatmap data
                heatmapData.legend = legendInfo.legend;
                heatmapData.palette = legendInfo.palette;
                heatmapData.paletteMax = legendInfo.paletteMax;
                heatmapData.paletteMin = legendInfo.paletteMin;

                this.setState({
                    heatmapData: heatmapData,
                    selectedCell: null,
                });

            } else if (portKey == PortKeys.select) {

                let selectedCell: [number, number] = null;
                if (port.LastStateStatus == StateStatus.NoData || isStringEmpty(port.State)) {

                    // fallback to default data
                    selectedCell = _.cloneDeep(props.WidgetDefaultValues[PortKeys.select]);

                    if (!isDefined(selectedCell))
                        return;
                }
                else if (port.LastStateStatus == StateStatus.Valid) {
                    selectedCell = port.State;
                }
                else
                    return;

                this.setState({ selectedCell: selectedCell });
            }
        });
    }
    // ---------------------------------------------------------------------------------------------
    private _getLegendInfo(widgetData: HeatmapData, legendPortState: LegendItem[] = null): LegendInfo {
        const { Legend } = widgetData;

        let colors: string[];
        // if 1 color to use; pass same color to color interpolation
        const rgb = decomposeColor(widgetData.BgColor).values.slice(0, 3);
        const colorFrom = `rgba(${[...rgb, 0].join(',')})`;
        const colorTo = `rgba(${[...rgb, 1].join(',')})`;
        colors = [colorFrom, colorTo];

        // decide which legend items to keep, port overrides settings
        let legendItems: LegendItem[] = isDefined(legendPortState) ? legendPortState : _.cloneDeep(Legend.Items);

        // do we show a legend?
        if (legendItems.length == 0)
            return {
                legend: null,
                palette: null,
                paletteMax: null,
                paletteMin: null
            };

        // calculate min,max of legend's items
        let min = Number.MAX_VALUE;
        let max = Number.MIN_VALUE;

        for (let item of legendItems) {
            if (min > item.value)
                min = item.value;
            if (max < item.value)
                max = item.value;
        }

        if (Legend.UseMainColor) {
            // fix item's color
            for (let item of legendItems) {
                item.color = `rgba(${[...rgb, opacity(item.value, min, max)].join(',')})`;
            }
        }
        else
            colors = _.map(legendItems, (item) => item.color);

        return {
            legend: Legend.Visible ? legendItems : null,
            palette: colorInterpolate(colors),
            paletteMax: max,
            paletteMin: min
        };
    }
    // ---------------------------------------------------------------------------------------------
    _cellRender = (value) => null;
    _cellStyle = (background: string, value: number, min: number, max: number, data: number[][], xi: number, yi: number) => {
        const { palette, paletteMax, paletteMin } = this.state.heatmapData;
        if (!isFunction(palette))
            // fallback and use main background
            return {
                background: background,
                opacity: opacity(value, min, max)
            };

        return { background: palette(opacity(value, paletteMin, paletteMax)) };
    }
    // ---------------------------------------------------------------------------------------------
    _onClick = (yi, xi) => {
        if (!isDefined(this.props.SendPortEvent))
            return;

        const { heatmapData } = this.state;
        this.props.SendPortEvent(PortKeys.onCellSelect, JSON.stringify({
            data: heatmapData.data[yi][xi],
            xLabel: heatmapData.xLabels[xi],
            yLabel: heatmapData.yLabels[yi],
            yi: yi,
            xi: xi,
        } as SelectedCellJson));

        this.setState({ selectedCell: [yi, xi] });
    }
}
// =================================================================================================
class HeatmapSettings extends PureComponent<IWidgetSettingsProps<HeatmapData>> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<HeatmapData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<HeatmapData>[] = [
            {
                name: '',
                elements: [
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Heatmap color',
                        tooltip: 'Main background color of heatmap.',
                        dataPath: 'BgColor',
                    },
                    {
                        type: SettingsType.Group,
                        name: 'Cell',
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Square',
                                tooltip: 'Whether cells will be square or not.',
                                dataPath: 'Cell.Square',
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Minimum size',
                                tooltip: 'Minimum cell\'s size in pixels',
                                dataPath: 'Cell.W',
                                minValue: 5,
                                maxValue: 100,
                                stepValue: 1,
                                valuePosfix: 'px',
                                isHidden: (data) => !data.Cell.Square
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Minimum width',
                                tooltip: 'Minimum cell\'s width in pixels',
                                dataPath: 'Cell.W',
                                minValue: 5,
                                maxValue: 100,
                                stepValue: 1,
                                valuePosfix: 'px',
                                isHidden: (data) => data.Cell.Square
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Minimum height',
                                tooltip: 'Minimum cell\'s height in pixels',
                                dataPath: 'Cell.H',
                                minValue: 5,
                                maxValue: 100,
                                stepValue: 1,
                                valuePosfix: 'px',
                                isHidden: (data) => data.Cell.Square
                            },
                            {
                                type: SettingsType.String,
                                label: 'Unit',
                                tooltip: 'Value\'s unit to be shown in tooltip.',
                                dataPath: 'Cell.Unit',
                            }
                        ]
                    },
                    {
                        type: SettingsType.Group,
                        name: 'Axis',
                        elements: [
                            {
                                type: SettingsType.ColorPicker,
                                label: "Font Color",
                                dataPath: "AxisFontColor"
                            },
                            {
                                type: SettingsType.Group,
                                name: 'X-Axis',
                                elements: [

                                    {
                                        type: SettingsType.DropDown,
                                        label: 'Location',
                                        tooltip: 'Location of the X-Axis. Available options "Top" and "Bottom"',
                                        dataPath: 'XLabelsLocation',
                                        items: [
                                            { label: 'Top', value: 'top' },
                                            { label: 'Bottom', value: 'bottom' },
                                        ]
                                    }

                                ]
                            },
                            {
                                type: SettingsType.Group,
                                name: 'Y-Axis',
                                elements: [
                                    {
                                        type: SettingsType.Boolean,
                                        label: 'Show Y-Axis',
                                        tooltip: 'Whether to show Y-Axis or not',
                                        dataPath: 'YLabels.Visible',
                                    },
                                    {
                                        type: SettingsType.Slider,
                                        label: 'Labels width',
                                        tooltip: 'Axis width in pixels',
                                        dataPath: 'YLabels.Width',
                                        minValue: 5,
                                        maxValue: 200,
                                        stepValue: 1,
                                        valuePosfix: 'px',
                                        isHidden: (data) => !data.YLabels.Visible
                                    },

                                    {
                                        type: SettingsType.DropDown,
                                        label: 'Alignment',
                                        tooltip: 'Label\'s alignment. Available options: "Right", "Left" and "Center".',
                                        dataPath: 'YLabels.Align',
                                        items: [
                                            { label: 'Right', value: 'right' },
                                            { label: 'Left', value: 'left' },
                                            { label: 'Center', value: 'center' },
                                        ],
                                        isHidden: (data) => !data.YLabels.Visible
                                    },

                                ]
                            }
                        ]
                    },
                    {
                        type: SettingsType.Group,
                        name: 'Legend',
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Show legend',
                                tooltip: 'tbd',
                                dataPath: 'Legend.Visible',
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Width',
                                tooltip: 'Legend\'s width in pixels',
                                dataPath: 'Legend.Width',
                                minValue: 5,
                                maxValue: 200,
                                stepValue: 1,
                                valuePosfix: 'px',
                                isHidden: (data) => !data.Legend.Visible
                            },
                            {
                                type: SettingsType.Boolean,
                                label: 'Use main background color',
                                tooltip: 'Disable if you want to set custom color per legend entry.',
                                dataPath: 'Legend.UseMainColor'
                            },
                            {
                                type: SettingsType.ItemsMgmtDialog,
                                label: 'Items',
                                tooltip: 'Legend\'s items',
                                dataPath: 'Legend.Items',
                                dialogStaticSize: { w: 0.75, h: 0.6 },
                                isHidden: (data) => !data.Legend.Visible,
                                itemsInfo: [
                                    {
                                        type: SettingsType.String,
                                        label: 'Label',
                                        dataPath: 'label',
                                        isHiddenCell: (data, r: LegendItem, i) => r.invisible,
                                    },
                                    {
                                        type: SettingsType.Boolean,
                                        label: 'Label hidden',
                                        dataPath: 'invisible'
                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        label: 'Color',
                                        dataPath: 'color',
                                        disableAlpha: true,
                                        isHiddenCell: (data, r, i) => data.Legend.UseMainColor,
                                    },
                                    {
                                        type: SettingsType.Number,
                                        isDecimal: true,
                                        label: 'Value',
                                        dataPath: 'value',
                                    },
                                ],
                                createNewItem: (data) => {
                                    return {
                                        color: 'rgb(0, 0, 0)',
                                        value: 0,
                                    } as LegendItem
                                },
                            }
                        ]
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={this.onSaveData}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private onSaveData = (data) => {
        if (isDefined(this.props.onWidgetDataUpdate))
            this.props.onWidgetDataUpdate(data);
    }
}
// =================================================================================================

const enum PortKeys {
    data = 'data',
    onCellSelect = 'onselect',
    select = 'select'
}
// ---------------------------------------------------------------------------------------------
export class HeatmapWidget extends IWidgetThingsClass<HeatmapData> {
    Render(key: string, width: number, height: number): ReactNode {
        return <Heatmap />;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <HeatmapSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];

        ports.push(new WebThingPort(
            {
                Name: 'Heatmap',
                PortKey: PortKeys.data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.data, data))
        );

        ports.push(new WebThingPort(
            {
                Name: 'On cell select',
                PortKey: PortKeys.onCellSelect,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Trigger)
        );

        ports.push(new WebThingPort(
            {
                Name: 'Select cell',
                PortKey: PortKeys.select,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.select, data))
        );

        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        // Add thing's ports.
        webThing.Ports = {};
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts();
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): HeatmapData {
        return {
            AxisFontColor: "black",
            BgColor: 'rgb(66, 86, 244)',
            Cell: {
                W: 10,
                H: 10,
                Square: false,
                Unit: ''
            },
            YLabels: {
                Visible: true,
                Width: 50,
                Align: 'right'
            },
            Legend: {
                Visible: false,
                Width: 100,
                Items: [],
                UseMainColor: true,
            },
            XLabelsLocation: 'bottom',
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return HeatmapWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: 'Heatmap',
            WidgetTitle: '',
            Key: 'HeatmapWidget',
            Type: 'HeatmapWidget',
            Data: HeatmapWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/heatmap.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};
        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.data)
            return dataPortSchemaValidator().DataValidateString(value);
        else if (key == PortKeys.select) {
            return selectPortSchemaValidator().DataValidateString(value);
        }
        return { valid: true, errors: '' };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: HeatmapData): HeatmapData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() {
        return {
            [PortKeys.data]: {
                xLabels: [],
                yLabels: [],
                data: []
            } as HeatmapDataJson,
            [PortKeys.select]: [-1, -1]
        }
    }
    // ---------------------------------------------------------------------------------------------
}

// ---------------------------------------------------------------------------------------------
function dataPortSchemaValidator() {
    return JsonSchemaValidator.GetInstance('heatmapDataPortSchemaValidator',
        {
            type: 'object',
            properties: {
                data: {
                    type: 'array',
                    items: {
                        type: 'array',
                        items: {
                            type: ['number', 'string', 'null']
                        }
                    }
                },
                xLabels: {
                    type: 'array',
                    items: { type: ['string', 'number'] }
                },
                yLabels: {
                    type: 'array',
                    items: { type: ['string', 'number'] }
                },
                legend: {
                    type: 'array',
                    items: {
                        type: 'object',
                        propepties: {
                            label: { type: 'string' },
                            color: { type: 'string' },
                            invisible: { type: 'boolean' },
                            value: { type: 'number' },
                        },
                        required: ['color']
                    }
                }
            },
            required: ['data']
        },
        (state: HeatmapDataJson) => {
            const errors = [];

            if (isDefined(state.yLabels)) {
                // check for rows mismatch
                const yLength = isDefined(state.data) ? state.data.length : 0;
                if (state.yLabels.length !== yLength)
                    errors.push(`yLabels: length mismatch. Data\'s row length is ${yLength} but yLabels.length is ${state.yLabels.length}`)
            }

            if (isDefined(state.xLabels)) {
                // if check for columns mismatch
                const xLength = isDefined(state.data) && isDefined(state.data[0]) ? state.data[0].length : 0;
                if (state.xLabels.length !== xLength)
                    errors.push(`xLabels: length mismatch. Data\'s column length is ${xLength} but yLabels.length is ${state.xLabels.length}`)
            }

            if (isDefined(state.legend)) {
                // check for color values
                _.forEach(state.legend, (item, index) => {
                    if (IsNullOrEmpty(item.color))
                        errors.push(`legend.[${index}].color: null or empty value`);

                    const color = decomposeColor(item.color);
                    if (!color.isValid)
                        errors.push(`legend.[${index}].color: '${item.color}' not a supported color format`);

                })
            }

            return {
                valid: errors.length == 0,
                errors: errors.join(',\n ')
            };

        });
}
function selectPortSchemaValidator() {
    return JsonSchemaValidator.GetInstance('selectPortSchemaValidator',
        {
            type: 'array',
            items: {
                type: 'number',
                minItems: 2,
                maxItems: 2
            }
        });
}
// ---------------------------------------------------------------------------------------------
